import { useTranslation } from "react-i18next";

import { useConsent } from "@/hooks/useConsent";
import { useLeaving } from "@/hooks/useLeaving";
import { Card, CardContent } from "@/components/ui/card";

import { useAuthSession } from "@/hooks/useAuthSession";
import { cn } from "@/engine/support/classNames";
import { useRoomPeek } from "@/hooks/useRoomPeek";

import { CodeScene } from "./EntryScene";
import { EntryWelcome } from "./EntryWelcome";
import { EntryRoomPreview, EntryRoomRoster } from "./EntryRoomPreview";
import { AppShell } from "@/components/AppShell";
import { CharacterCard } from "@/components/profile/CharacterCard";
import { HomeRooms } from "./HomeRooms";
import { EntrySteps } from "./EntrySteps";
import {
  type EntryPageProps,
  EntryTopBar,
  EntryDoors,
  panelChangesQuietly,
  useEntryDoor,
  useInvitedRoomPreview,
  useCardHeightAnimation,
  useEntryIdentity,
  useInvitedRoomCode,
} from "./entryPageParts";
import { CreateTab, JoinTab } from "./entryTabs";
import { useSwitchedOnce } from "./useSwitchedOnce";
import { entryMotion } from "./entryMotion";
import {
  entryShell,
  peekedCode,
  useEntryPanel,
  type EntryPanel,
} from "./entryPageParts";

export type { EntryPageProps };

/** What the card holds: a tab, or a visitor already on their way into a room. */

const noop = () => {};

const ENTRY_DEFAULTS = {
  prefillCode: "",
  entryError: null,
  walkIn: null,
  onCameraSelect: noop,
} satisfies Partial<EntryPageProps>;

/**
 * What home says before its doors: the welcome on a first visit, a signed-in
 * visitor's rooms — which are the page, not furniture on a rail (#738) — and
 * the character a room will see, with the way to the profile that changes it.
 */
function HomeHead({
  firstVisit,
  provisional,
}: {
  firstVisit: boolean;
  provisional: boolean;
}) {
  return (
    <>
      <EntryWelcome shown={firstVisit} provisional={provisional} />
    </>
  );
}

/**
 * The rooms a signed-in visitor can go back to, under the way in (#776): home
 * is who you are, then where you are going, then where you have been.
 */
function HomeFoot({
  signedIn,
  rooms,
  counts,
  onOpen,
  onForget,
}: {
  signedIn: boolean;
  rooms: EntryPageProps["savedRooms"];
  counts: EntryPageProps["roomCounts"];
  onOpen: (code: string) => void;
  onForget: EntryPageProps["onForgetRoom"];
}) {
  const { t } = useTranslation();
  if (!signedIn) return null;
  return (
    <div className="mt-8 space-y-4">
      <h2 className="text-xl font-semibold tracking-tight">
        {t("entry.yourRooms")}
      </h2>
      <HomeRooms
        rooms={rooms ?? []}
        counts={counts}
        onOpen={onOpen}
        onForget={onForget}
      />
    </div>
  );
}

/**
 * How wide home is: the demo's page (#769), for everybody since the character
 * is edited on it (#776).
 */
const homeWidth = () => "mx-auto max-w-5xl";

/**
 * Where Back leads (#832): from the page where a room is lit, a step back to
 * the way in; from the room an invitation answered, home without its code; and
 * from home itself nowhere, so no Back is drawn.
 */
function backFrom(
  asPage: boolean,
  showsRoom: boolean,
  toWayIn: () => void,
): "home" | (() => void) | undefined {
  if (asPage) return toWayIn;
  return showsRoom ? "home" : undefined;
}

/**
 * The way in under a wide home keeps the card's width, centred. Beside the
 * character it is a column whose card takes the rest of the height (#821), so
 * the two end on one line whichever of them is the taller.
 */
const wayInWidth = () => "w-full min-w-0 lg:flex lg:flex-col";

export function EntryPage(props: EntryPageProps) {
  const { t } = useTranslation();
  const {
    onCreateRoom,
    onJoinRoom,
    onTryDemo,
    savedRooms,
    roomCounts,
    onForgetRoom,
    onCancelEntry,
    isConnecting,
    prefillCode,
    entryError,
    walkIn,
    selectedMicId,
    selectedCameraId,
    onMicSelect,
    onCameraSelect,
  } = { ...ENTRY_DEFAULTS, ...props };

  // Withdrawing consent stays as easy as giving it. The choice itself is owed
  // before the way in on a room's threshold, which is what connects now.
  const { reopen: reopenConsent } = useConsent();

  // The name is asked at the room's threshold; here it only labels the rail.
  const { effectiveName } = useEntryIdentity();

  const {
    invitedCode,
    roomCode,
    setRoomCode,
    joinCode,
    joinsDemoRoom,
    codeToJoin,
  } = useInvitedRoomCode(prefillCode);

  // Nothing is claimed about the account until the first answer arrives.
  const { session, ready: accountKnown } = useAuthSession();
  const signedIn = Boolean(session);

  // Creating moves to a line under the card where no door is drawn, and taking
  // that line counts as choosing, which is what brings the door back.
  const {
    activeTab,
    setActiveTab,
    oneWayIn,
    chosenByVisitor,
    firstVisit,
    provisional,
  } = useEntryDoor(
    invitedCode,
    { known: accountKnown, signedIn },
    props.opensOn,
  );

  // What the card is showing: a tab, or the visitor being walked in — whether
  // they pressed the way in or came back from a sign-in round trip. The two are
  // one sequence of panels in one card rather than a screen replacing a screen.
  //
  // A press keeps its door until the waits are worth drawing, which is almost
  // always never: the access check that holds the way in usually answers in a
  // few milliseconds, and the room arrives before the door has moved.
  const { walking, panel, busy } = useEntryPanel(
    activeTab,
    walkIn,
    isConnecting,
  );
  const motion = entryMotion(useSwitchedOnce(panel));

  // The door the account's answer chose arrives without a transition: nobody
  // pressed anything, and a panel sliding away would be a flicker they did not cause.
  const { leaving: leavingPanel, ref: leavingPanelRef } = useLeaving(
    panel,
    panelChangesQuietly(chosenByVisitor, walking),
  );

  // An empty code opens the demo, which is this browser's own address rather
  // than a room on the server (#656): nothing is looked up and nobody is
  // joined, so the press navigates rather than entering. Every other press
  // goes to the room's threshold, which asks who the visitor is.
  const handleJoin = () =>
    joinsDemoRoom ? onTryDemo() : onJoinRoom(codeToJoin);
  const handleCreate = (name: string) => onCreateRoom(name);

  const { cardInnerRef, cardHeight, reservedHeight } =
    useCardHeightAnimation(panel);

  // What is behind the code being typed. Read-only: looking at a door is not
  // going through it, so nothing here joins the room or publishes a name.
  const peek = useRoomPeek(peekedCode(panel, props.peekEnabled, joinCode));
  // The room, rather than a form, for the nine in ten who arrived on a link to
  // a room that answered.
  const { showsRoom, avatar } = useInvitedRoomPreview(activeTab, peek, walking);

  // The screen where a room is opened is a page too, and wants the same shell:
  // the four pictures at their drawn size need the width, and a rail squeezed
  // beside them would be the same mistake the room made.
  const { asPage, onTheShell, wide } = entryShell(
    panel,
    accountKnown,
    signedIn,
    showsRoom,
  );

  // Rendered twice while a switch is running: once arriving, once leaving.
  const panelFor = (which: EntryPanel) =>
    which === "entering" ? (
      // The waiting panel takes the door's place in the same card, so this is
      // a change of content rather than a new screen — and the three waits the
      // spinner used to stand for now say which one is being waited on.
      <div
        data-testid="entry-entering"
        className="flex flex-col items-center justify-center"
      >
        <p className="text-sm text-muted-foreground">{t("entry.entering")}</p>
        <EntrySteps onCancel={onCancelEntry} />
      </div>
    ) : which === "create" ? (
      <CreateTab
        accountKnown={accountKnown}
        signedIn={signedIn}
        isConnecting={busy}
        onCreate={handleCreate}
      />
    ) : (
      <JoinTab
        roomCode={roomCode}
        setRoomCode={setRoomCode}
        joinsDemoRoom={joinsDemoRoom}
        isConnecting={busy}
        entryError={entryError}
        onJoin={handleJoin}
        onHandedOver={props.onHandedOver}
      />
    );

  // What the middle of the frame is holding. Three things, one of which is
  // always on show; the frame around them does not know which.
  const middle = asPage ? (
    /* The screen where a room is opened is a page, not a card in the middle of
       one: §06 gives it the four pictures at their drawn size and a flat ground
       behind them. */
    <div data-testid="entry-create-page" className="flex flex-col gap-4">
      {/* The four pictures are the only thing on this screen that wants the
          page's width. Everything else is the same object it is on the other
          door and takes the card's width, centred on the page's axis: a door
          a metre wide and a way in to match read as a browser window
          somebody forgot to size, not as a screen. */}
      <div className="mx-auto flex w-full max-w-md flex-col gap-2 sm:gap-3">
        {/* There is no rail to carry the other door (#738), so the page
            carries the way back itself — to joining by code. */}
        <EntryDoors
          activeTab={activeTab}
          onChoose={setActiveTab}
          disabled={Boolean(walkIn)}
          oneWayIn={false}
        />
      </div>
      <CreateTab
        accountKnown={accountKnown}
        signedIn={signedIn}
        isConnecting={busy}
        onCreate={handleCreate}
      />
    </div>
  ) : showsRoom ? (
    /* The room they were invited to, instead of a form. The way in goes inside
       it rather than being rebuilt: same press, same name, same level bar as
       the card's. */
    <EntryRoomPreview
      code={joinCode}
      peek={peek}
      avatar={avatar}
      onCreateInstead={() => setActiveTab("create")}
    >
      <div className="flex flex-col gap-3">
        <JoinTab
          roomCode={roomCode}
          setRoomCode={setRoomCode}
          joinsDemoRoom={joinsDemoRoom}
          isConnecting={busy}
          entryError={entryError}
          onJoin={handleJoin}
          onHandedOver={props.onHandedOver}
        />
      </div>
    </EntryRoomPreview>
  ) : (
    <>
      {/* Only the Join door has a code to look behind; the create screen is
            about a room that does not exist yet.

            Keyed off the door rather than off the panel on show: the panel goes
            to the waits while somebody is being walked in, and the strip must
            not stop naming the room at the moment they are entering it. */}
      <HomeHead firstVisit={firstVisit} provisional={provisional} />
      {/* Who you are above where you are going: the character is a compact
          link to the profile, and a column of its own beside the way in
          would be a card over an empty gap on a wide screen. */}
      <div className="grid gap-4">
        <CharacterCard />
        {/* The way in keeps the card's width under a home as wide as the demo's
          (#769): a code field a page wide is a window nobody sized. */}
        <div className={wayInWidth()}>
          {/* The fire reports the room behind a code, and is drawn only then. */}
          <CodeScene peek={peek} code={joinCode} />

          <EntryDoors
            activeTab={activeTab}
            onChoose={setActiveTab}
            disabled={Boolean(walkIn)}
            oneWayIn={oneWayIn}
          />

          {/* Main card. The reserved area holds the tallest panel the card
            has drawn and does not give the space back, so a shorter panel
            resizes the card and moves nothing else — the column is centred in
            the window, and a column that shrinks takes half of it off the top,
            which is the scene and the avatar sliding at the press. The classes
            are the floor before the first measurement, on the server and in
            the frame before the layout effect runs.

            The card's height and the panel inside it are given the same
            duration, so a switch is one movement instead of content landing at
            the size the other tab left behind. The outgoing panel is taken out
            of the flow while it fades, which is what leaves the height free to
            follow the arriving one. */}
          <div
            data-testid="entry-card-space"
            className={motion.cardSpace}
            // Nought means nothing was measured — a hidden card, a server
            // render — and a reservation of nothing would take the floor
            // away rather than replace it.
            style={reservedHeight ? { minHeight: reservedHeight } : undefined}
          >
            <Card
              data-testid="entry-card"
              className={motion.card}
              style={{ height: cardHeight }}
            >
              <div className="relative">
                <div ref={cardInnerRef}>
                  <CardContent
                    key={panel}
                    data-testid={`entry-panel-${panel}`}
                    className={motion.panel}
                  >
                    {panelFor(panel)}
                  </CardContent>
                </div>
                {leavingPanel && (
                  <div
                    ref={leavingPanelRef}
                    inert
                    aria-hidden
                    data-testid={`entry-panel-leaving-${leavingPanel}`}
                    className="absolute inset-x-0 top-0 pointer-events-none motion-leave [--motion-duration:var(--motion-slow)]"
                  >
                    <CardContent className="p-3 sm:p-5 lg:p-6">
                      {panelFor(leavingPanel)}
                    </CardContent>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Where the doors are not drawn, creating is still reachable — as a
            line rather than as half of a switch, because it is the rarer case
            and this visitor was invited somewhere. */}
          {oneWayIn && (
            <button
              type="button"
              data-testid="entry-create-instead"
              // Inert for the same reason the doors are: a walk in is going on,
              // and changing your mind about which door you are at is not a
              // thing that can happen halfway through one.
              disabled={Boolean(walkIn)}
              onClick={() => setActiveTab("create")}
              className="sm:hidden mt-3 w-full text-center text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors disabled:pointer-events-none disabled:opacity-50"
            >
              {t("entry.createRoom")}
            </button>
          )}
        </div>
      </div>
      <HomeFoot
        signedIn={signedIn}
        rooms={savedRooms}
        counts={roomCounts}
        onOpen={onJoinRoom}
        onForget={onForgetRoom}
      />
    </>
  );

  const column = (
    <div
      data-testid="entry-column"
      data-panel={panel}
      className={cn(
        motion.column,
        // Both widths are written as `min-[...]` rather than one of them as
        // `sm:`: Tailwind sorts arbitrary width variants ahead of the named
        // breakpoints, so `sm:` would win at 1440px and the card would never
        // get its own.
        //
        // A middle that owns its area takes all of it. Everything else is an
        // object in the middle of the column, at the card's 448 — a create door
        // offering a sign-in, a code being typed, a room that answered nothing.
        onTheShell ? "flex min-h-0 flex-1 flex-col" : homeWidth(),
      )}
    >
      {/* The heading is not drawn. What says whose screen this is, to anybody
          who can see it, is the fire; the screen used to open on a pill with a
          flame and the product's name, which said only that this was software
          and would have suited any call. The strip cannot be the heading itself
          — it is a picture, and hidden from assistive technology — so the
          heading stays, unseen, and first in the order it is read in. */}
      <h1 className="sr-only">{t("app.name")}</h1>

      {middle}

      {/* Withdrawing consent has to be as easy as giving it. */}
      <div className="mt-4 flex items-center justify-center gap-3 text-center">
        <button
          type="button"
          data-testid="cookie-settings"
          className="text-xs text-muted-foreground/40 hover:text-muted-foreground/70 underline-offset-2 hover:underline transition-colors"
          onClick={reopenConsent}
        >
          {t("consent.cookieSettings")}
        </button>
        {/* A plain anchor: the entry screen is rendered outside a router in
            tests and stories, and a footer link must not depend on one. */}
        <a
          href="/privacy"
          data-testid="privacy-link"
          className="text-xs text-muted-foreground/40 hover:text-muted-foreground/70 underline-offset-2 hover:underline transition-colors"
        >
          {t("entry.privacy")}
        </a>
      </div>

      {/* Descriptive copy for crawlers, under the heading at the top of the
          column rather than carrying one of its own. */}
      <div className="sr-only">
        <p>{t("entry.pitch")}</p>
        <p>{t("entry.noInstall")}</p>
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        // Scrolls rather than clips: the welcome and the character card make
        // the way in taller than a phone, and a column centred in a window it
        // overflows loses its top under the bar.
        "h-dvh max-h-dvh overflow-y-auto bg-background flex flex-col relative",
        // The frame is the application's own and fills the window, under the
        // bar that spans it. Narrower than a rail, the card is an object on a
        // page and sits in the middle of one.
        wide
          ? "items-stretch justify-start pt-12"
          : "items-center justify-start px-3 pt-16 pb-6 sm:px-6",
      )}
    >
      <EntryTopBar
        selectedMicId={selectedMicId}
        selectedCameraId={selectedCameraId}
        onMicSelect={onMicSelect}
        onCameraSelect={onCameraSelect}
        joinCode={joinCode}
        back={backFrom(asPage, showsRoom, () => setActiveTab("join"))}
      />

      {wide ? (
        <AppShell
          testId="entry-shell"
          aside={
            showsRoom ? (
              <EntryRoomRoster
                taken={peek.kind === "answered" ? peek.count : 0}
                avatar={avatar}
                name={effectiveName}
              />
            ) : undefined
          }
        >
          {column}
        </AppShell>
      ) : (
        column
      )}
    </div>
  );
}
